<template>
  <div class="location-step" data-search-location-scroll-target>
    <pu-auto-suggest
      data-test="search-wizard-location-wrapper"
      :class="{
        desktop: $isDesktop,
        'menu-open': isMenuOpen,
        autosuggest: true,
      }"
      :get-suggestion-value="getInputValue"
      :input-props="inputProps"
      :section-configs="sectionConfigs"
      :should-render-suggestions="shouldRenderSuggestionsHandler"
      :suggestions="autosuggestSuggestions"
      :model-value="inputValue"
      autocomplete="on"
      @blur="inputBlur"
      @click.passive="inputClick"
      @closed="suggestionsClosed"
      @opened="suggestionsOpened"
      @focus="$emit('focus')"
      @update:model-value="inputChanged($event)"
    >
      <template #before-input>
        <div class="input-controls">
          <span class="icon-container mag">
            <img
              src="@/assets/images/magnify-blue.svg"
              :alt="$t('Search')"
              width="26"
              height="26"
            />
          </span>
          <span
            v-if="shouldShowClearInputButton"
            class="icon-container clear-input"
            data-test="search-input-clear-button"
            @click="clearInput"
            ><img src="@/assets/images/cross-circle.svg" />
          </span>
        </div>
      </template>

      <template #before-section-recentSearch>
        <translate tag="h4">Recent searches</translate>
      </template>

      <template #default="{ suggestion }">
        <location-wizard-suggestion-line
          :query="inputValue"
          :suggestion="suggestion"
          data-test="autosuggest-result"
        />
      </template>

      <template #after-section-recentSearch>
        <h4 v-if="suggestions.length > 1" class="suggestions-heading">
          Suggestions
        </h4>
      </template>
    </pu-auto-suggest>
    <translate
      v-if="showHelpText && !$isDesktop"
      tag="p"
      class="help-text"
      data-test="autocomplete-label"
    >
      Search for a location, site name, postcode
    </translate>
  </div>
</template>

<script lang="ts">
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import type {
  LocationWizardCampsiteData,
  LocationWizardCustomData,
  LocationWizardHierarchyData,
} from '../../../domain/LocationWizardData'
import {
  LocationSuggestionCampsite,
  LocationSuggestionCustom,
  LocationSuggestionHierarchy,
} from '../domain/LocationSuggestions'
import type { AutoSuggestData, LocationSuggestion } from '../types'

@Component({
  emits: [
    'input',
    'inputChanged',
    'focus',
    'clearInput',
    'autosuggestion-closed',
    'campsiteSelected',
    'hierarchySelected',
    'customLocationSelected',
  ],
})
class LocationSectionSuggestableInput extends Vue {
  @Prop({ default: '' })
  inputValue: string

  @Prop({ required: true })
  suggestions: AutoSuggestData

  @Prop()
  searchType: string

  @Prop({ required: true })
  focused: boolean

  @Prop()
  placeholder?: string

  @Prop()
  forceSuggestionsOpen: boolean

  @Prop({ default: true })
  showHelpText: boolean

  @Prop({ default: false })
  overrideHideClearable: boolean

  alreadySelected = false
  inputSize = 18
  private menuBlur = true
  private triggeredMenuOpen = false
  private hasScrolledInput = false
  sectionConfigs = {
    default: {},
    campsite: {
      limit: 3,
      onSelected: (
        { item }: { item: LocationSuggestionCampsite },
        term: string,
        selectingDefault: boolean,
      ) => {
        this.onCampsiteSelected(item, selectingDefault ? term : null)
      },
    },
    hierarchy: {
      limit: 3,
      onSelected: (
        { item }: { item: LocationSuggestionHierarchy },
        term: string,
        selectingDefault: boolean,
      ) => {
        this.onHierarchySelected(item, selectingDefault ? term : null)
      },
    },
    custom: {
      limit: 3,
      onSelected: (
        { item }: { item: LocationSuggestionCustom },
        term: string,
        selectingDefault: boolean,
      ) => {
        this.onCustomSelected(item, selectingDefault ? term : null)
      },
    },
    recentSearch: {
      limit: 3,
      onSelected: ({ item }) => {
        this.onRecentSearchSelected(item)
      },
    },
  }

  async mounted() {
    await this.onFocusedChange(this.focused)
    if (this.forceSuggestionsOpen) {
      this.showOverlay()
      this.searchInputElement.focus()
    }
  }

  shouldRenderSuggestionsHandler(size: number, loading: boolean) {
    if (this.forceSuggestionsOpen && size > 0 && loading) {
      // need to force click so the autosuggest component does its thing and listens on click
      this.searchInputElement.click()
      return true
    }
    // default autosuggest behaviour
    return size > 0 && !loading
  }

  get autosuggestSuggestions() {
    return this.suggestions
  }

  get shouldShowClearInputButton() {
    if (this.overrideHideClearable) {
      return false
    }
    if (this.searchType === 'CUSTOM') {
      return true
    }
    if (this.searchType === 'BOUNDS' || this.searchType === 'LOCATION') {
      return true
    }
    return false
  }

  get inputProps() {
    return {
      id: 'autosuggest__input',
      placeholder: this.placeholder,
      class: 'autosuggest-input',
      name: 'q',
      size: this.inputSize,
    }
  }

  get searchInputElement() {
    return document.querySelector('.autosuggest-input') as HTMLElement
  }

  private onCampsiteSelected(item: LocationSuggestionCampsite, term?: string) {
    this.alreadySelected = !term
    const { name, slug, inputValue, hierarchyPath } = item
    this.campsiteSelected(
      {
        dataType: 'campsite',
        name,
        slug,
        hierarchyPath,
        inputValue,
      },
      term,
    )
  }

  private onHierarchySelected(
    item: LocationSuggestionHierarchy,
    term?: string,
  ) {
    this.alreadySelected = !term
    const { name, path, point, type, bookableCount } = item
    this.hierarchySelected(
      {
        dataType: 'hierarchy',
        name,
        path,
        point,
        type,
        bookableCount,
      },
      term,
    )
  }

  private onCustomSelected(item: LocationSuggestionCustom, term?: string) {
    this.alreadySelected = !term
    const { name, mapboxId } = item
    this.customLocationSelected({ dataType: 'custom', name, mapboxId }, term)
  }

  @Watch('focused')
  async onFocusedChange(focused: boolean) {
    if (focused) {
      await this.$nextTick()
      // this.searchInputElement.focus()
      await this.scrollToSearchInput()
    }
  }

  campsiteSelected(data: LocationWizardCampsiteData, term?: string) {
    const campsiteSelected = { ...data, term }
    this.$emit('campsiteSelected', campsiteSelected)
    return campsiteSelected
  }

  hierarchySelected(data: LocationWizardHierarchyData, term?: string) {
    const hierarchySelected = { ...data, term }
    this.$emit('hierarchySelected', hierarchySelected)
    return hierarchySelected
  }

  customLocationSelected(data: LocationWizardCustomData, term?: string) {
    const customLocationSelected = { ...data, term }
    this.$emit('customLocationSelected', customLocationSelected)
    return customLocationSelected
  }

  onRecentSearchSelected(data: { url: string }) {
    this.alreadySelected = true
    this.$emit('recentSearchSelected', data.url)
    return data
  }

  inputBlur() {
    this.hideOverlay()
    this.$emit('autosuggestion-closed')
    this.$bubble('blur')
  }

  async inputClick() {
    await this.scrollToSearchInput()
  }

  suggestionsClosed() {
    this.hideOverlay()
    this.triggeredMenuOpen = false
    this.$emit('autosuggestion-closed')
    if (this.alreadySelected || !this.suggestions.length) {
      return
    }
    let suggestionType: string
    let item: LocationSuggestion | null
    if (this.shouldSetCustomOnBlur) {
      suggestionType = 'custom'
      item = this.customSuggestionsDefaultItem
    } else {
      suggestionType = this.autosuggestSuggestions.filter(
        (el) => el.name !== 'recentSearch',
      )[0]?.name
      item = this.defaultSuggestion
    }
    if (!item || !suggestionType) {
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for getSuggestionNameSelector return type
    const selectorTrigger = this.getSuggestionNameSelector(suggestionType)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- selectorTrigger type needs refinement
    selectorTrigger(item, this.inputValue)
  }

  suggestionsOpened() {
    this.alreadySelected = false
    this.triggeredMenuOpen = true
  }

  private get shouldSetCustomOnBlur() {
    return (
      this.suggestions.length &&
      this.autosuggestSuggestions[0].name === 'campsite' &&
      this.customSuggestions
    )
  }

  private get customSuggestionsDefaultItem() {
    return this.customSuggestions!.data.length
      ? this.customSuggestions!.data[0]
      : null
  }

  private get defaultSuggestion() {
    const suggestions = this.suggestions.filter(
      (el) => el.name !== 'recentSearch',
    )
    return suggestions[0]?.data.length ? suggestions[0].data[0] : null
  }

  private get customSuggestions() {
    const suggestions = this.suggestions.filter((s) => s.name === 'custom')
    return suggestions.length ? suggestions[0] : undefined
  }

  private getSuggestionNameSelector(type: string) {
    const suggestionNameToSelectorMap = {
      campsite: this.onCampsiteSelected,
      hierarchy: this.onHierarchySelected,
      custom: this.onCustomSelected,
      recentSearch: this.onRecentSearchSelected,
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: Add proper return type for suggestion selector map
    return suggestionNameToSelectorMap[type]
  }

  async scrollToSearchInput() {
    if (!this.hasScrolledInput) {
      this.hasScrolledInput = true
      const elementToScrollTo = this.$isDesktop
        ? '[data-desktop-location-scroll-target]'
        : '[data-search-location-scroll-target]'
      // wait for soft keyboard to open
      await new Promise((resolve) => setTimeout(resolve, 300))
      this.$puScrollTo(elementToScrollTo, {
        duration: 300,
        offset: -18,
      })
    }
  }

  clearInput() {
    this.$emit('inputChanged', '')
    this.$emit('clearInput', true)
    this.hideOverlay()
    this.searchInputElement.focus()
  }

  inputChanged(input: string) {
    this.$emit('inputChanged', input)
    this.showOverlay()
  }

  get isMenuOpen() {
    return (
      (this.suggestions.length && this.inputValue.length && !this.menuBlur) ||
      this.triggeredMenuOpen
    )
  }

  getInputValue({ name, item }: { name: string; item: LocationSuggestion }) {
    return name === 'campsite'
      ? (item as LocationSuggestionCampsite).inputValue
      : (item as LocationSuggestionHierarchy).name
  }

  private showOverlay() {
    this.menuBlur = false
  }

  private hideOverlay() {
    this.menuBlur = true
  }
}

export default toNative(LocationSectionSuggestableInput)
</script>
<style scoped>
/* cursor pointer for this bug: https://github.com/darrenjennings/vue-autosuggest/issues/67#issuecomment-506191156 */
.location-step {
  cursor: pointer;
}

label {
  padding: 0 0 0.5em;
  display: block;
  color: var(--colour-blue);
}

.autosuggest {
  position: relative;
}

.autosuggest.menu-open:after {
  content: '';
  background: white;
  position: absolute;
  z-index: 11;
  left: -0.9em;
  right: 0.9em;
  width: calc(100vw - 1em);
  top: -1em;
  bottom: -0.5em;
  border-radius: 0.5em 0.5em 0 0;
}

.autosuggest :deep(.autosuggest-input) {
  -webkit-appearance: none;
  background: white;
  border-radius: 4em;
  border: 2px solid #6496c8;
  box-sizing: border-box;
  display: flex;
  font-size: 1em;
  height: auto;
  margin: 0;
  padding: 0.4em 2em 0.4em 2.2em;
  position: relative;
  text-overflow: ellipsis;
  width: 100%;
  z-index: 12;
}

@media (max-width: 320px) {
  .autosuggest :deep(.autosuggest-input) {
    font-size: 16.4px;
  }
}

.autosuggest :deep(.autosuggest__results-item--highlighted),
.autosuggest :deep(.autosuggest__results-item--highlighted:active) {
  background-color: var(--colour-page-background);
}

.autosuggest :deep(.autosuggest__results-container) {
  position: relative;
}

.autosuggest.menu-open :deep(.autosuggest__results-container:after) {
  background: rgba(0, 0, 0, 0.5);
  bottom: 0;
  content: '';
  cursor: initial;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 11;
}

.autosuggest :deep(.autosuggest__results) {
  background-color: white;
  border-radius: 0 0 0.5em 0.5em;
  left: -0.9em;
  padding: 0.5em 0 1.4em;
  position: absolute;
  right: 0.9em;
  width: calc(100vw - 1em);
  z-index: 12;
}

.input-controls {
  align-items: center;
  display: flex;
  flex-direction: row;
  font-size: 1em;
  height: 100%;
  justify-content: space-between;
  padding: 0 0.4em 0 0.55em;
  position: absolute;
  width: 100%;
}

.icon-container {
  cursor: pointer;
  display: block;
  width: 1.45em;
  z-index: 13;
}

.help-text {
  color: var(--colour-blue);
  font-size: var(--small);
  margin: 0 -1em;
  padding: 0.2em;
  text-align: center;
}

/* desktop & tablet */
.autosuggest.desktop.menu-open:after {
  content: none;
}

.autosuggest.desktop :deep(.autosuggest__results) {
  border-radius: 0.5em;
  left: 0;
  right: 0;
  width: 200%;
}

.autosuggest.desktop :deep(.autosuggest-input) {
  min-height: 3.1em;
  z-index: 1;
  padding: 0.5em 2em 0.5em 2.4em;
  border: 3px solid #0072bc;
  border-radius: 9em;
}

.autosuggest.desktop :deep(.icon-container) {
  z-index: 2;
}

.autosuggest.desktop.menu-open :deep(.autosuggest-input) {
  z-index: 12;
}

.autosuggest.desktop.menu-open :deep(.icon-container) {
  z-index: 13;
}

h4 {
  font-size: var(--small);
  color: #999;
  padding: 0.2em 0 0 1em;
}

.suggestions-heading {
  border-top: 1px solid black;
  padding-top: 0.5em;
}

@media (hover: hover) {
  .clear-input:hover {
    filter: brightness(0.5);
  }
}
</style>
